// ============================================================
// ROAS FORECAST ENGINE
//
// Computes ROAS forecasts for individual ad entities.
//
// PIPELINE (sequential stages):
//   ┌────────────────────────────────────────────────────────┐
//   │ STAGE 1: HISTORICAL DATA EXTRACTION (AD LEVEL)        │
//   │   • Parse transactions → daily spend & revenue per ad │
//   │   • Map each transaction to matched ad entity         │
//   │   • Compute daily ROAS per ad                         │
//   ├────────────────────────────────────────────────────────┤
//   │ STAGE 2: PER-AD FORECAST GENERATION (PHASE-AWARE)      │
//   │   • Detect Lifecycle Phase (Testing, Scaling, etc.)    │
//   │   • Fit ad fatigue half-life                          │
//   │   • Compute adaptive jitter profile                   │
//   │   • Run backtesting                                   │
//   │   • Generate DES forecast with phase-aware params:    │
//   │       - Bayesian shrinkage                            │
//   │       - Phase-tuned smoothing coefficients            │
//   │       - Damped trend                                  │
//   │       - Detrended day-of-week seasonality             │
//   │       - Incremental ad fatigue                        │
//   ├────────────────────────────────────────────────────────┤
//   │ STAGE 3: COMBINED ROAS (revenue-weighted blend)       │
//   │   • Historical: Total Revenue / Total Spend per day   │
//   │   • Forecast: spend-weighted average of per-ad        │
//   │     forecast baselines                                │
//   └────────────────────────────────────────────────────────┘
//
// ============================================================

import { format, parseISO, addDays } from 'date-fns';

import {
    RoasPoint,
    RoasForecastResult,
    RoasSourceForecast,
    RawForecastPoint,
    JitterProfile,
    PhaseInfo
} from './types';
import { MARKETING_PLATFORMS, SOURCE_COLORS } from './constants';
import { normalRandom } from './math';
import { computeAdaptiveJitter } from './adaptiveJitter';
import { fitFatigueHalfLife, getDefaultHalfLife } from '../../charts/predictionUtils';
import { BoardEntity } from '../../../../../stores/boardStore';
import { detectCurrentPhase } from './phaseDetector';
import { findCampaignMatch } from '../../../lib/OriginResolver';

// ============================================================
// INTERNAL: DES Forecast Generator
// ============================================================

/**
 * Run a Double Exponential Smoothing (DES) forecast on a ROAS series.
 *
 * This is the core forecasting function for paid sources.
 * It produces `days` forecast points with:
 *   - Baseline: DES level + damped trend + seasonality + fatigue + phase-awareness
 *   - Noise: multiplicative gaussian noise scaled by CV
 *   - Bounds: confidence interval expanding with √(1 + 0.12·t)
 */
const forecastFromSeries = (
    validRoas: number[],
    halfLife: number,
    sourceName: string,
    dates: string[],
    fullDailyRoas: (number | null)[],
    days: number,
    accountPrior: number,
    jitterCV: number,
    jitterNoiseMult: number,
    jitterBandMult: number,
    phase: PhaseInfo,
    activityDensity: number
): RawForecastPoint[] => {
    // ---- PAUSED GUARD ----
    if (phase.current === 'paused') {
        return new Array(days).fill(0).map(() => ({
            baseline: 0,
            noise: 0,
            upper: 0,
            lower: 0
        }));
    }

    // ---- PHASE-AWARE HYPERPARAMETERS ----
    // Adjust smoothing factors based on the ad's detected phase.
    let alpha = 0.15, beta = 0.05, phi = 0.95;

    switch (phase.current) {
        case 'testing':
            alpha = 0.25; // React faster to new test signals
            beta = 0.02;  // Dampen trend (don't trust short-term growth)
            break;
        case 'scaling':
            alpha = 0.20;
            beta = 0.10;  // Allow stronger trend for scaling ads
            break;
        case 'stabilizing':
            alpha = 0.10;
            beta = 0.02;  // Keep steady for stable ads
            break;
        case 'sunset':
            alpha = 0.30;
            beta = 0.05;  // React quickly to the drop
            phi = 0.85;   // Faster trend damping
            break;
        case 'learning':
            alpha = 0.10; // More conservative
            beta = 0.01;
            break;
    }

    // ---- LOCAL STATISTICS (computed first for cap calculations) ----
    const localMean = validRoas.reduce((a, b) => a + b, 0) / validRoas.length;
    const historicalMax = Math.max(...validRoas);
    // ECOMMERCE: 0.1x ROAS improvement is significant. Cap tightly.
    const softCap = historicalMax * 1.2;

    // ---- ECOMMERCE-GRADE BAYESIAN INITIALIZATION ----
    // With 2 data points → 67% trust in observed data, 3+ → 100%.
    // In ecommerce with sparse product data, the observed values ARE the signal.
    const initTrust = Math.min(1, validRoas.length / 3);

    // Cap the account prior: at most 20% above the source's observed average.
    // A product at 0.4x ROAS should forecast ~0.4x, NOT 0.8x or 1.0x.
    const cappedPrior = Math.min(accountPrior, localMean * 1.2);

    // Seed level: heavily weighted toward observed average
    let level = (initTrust * localMean) + ((1 - initTrust) * cappedPrior);
    let trend = 0;

    // Trust factor for uncertainty/trend damping (uses density penalty)
    const baseTrust = Math.min(1, validRoas.length / 10);
    const trustFactor = baseTrust * Math.pow(activityDensity, 2);

    // Seed trend only with >= 7 data points (2 points has zero trend signal)
    if (validRoas.length >= 7) {
        const window = validRoas.slice(-Math.min(14, validRoas.length));
        const n = window.length;
        let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
        for (let k = 0; k < n; k++) { sumX += k; sumY += window[k]; sumXY += k * window[k]; sumXX += k * k; }
        const denom = n * sumXX - sumX * sumX;
        trend = denom !== 0 ? (n * sumXY - sumX * sumY) / denom : 0;
    }

    // Run DES through all data
    for (let k = 1; k < validRoas.length; k++) {
        const prev = level;
        level = alpha * validRoas[k] + (1 - alpha) * (level + trend);
        trend = beta * (level - prev) + (1 - beta) * trend;
    }

    // Cap level after DES
    level = Math.min(level, softCap);

    // For < 5 data points, zero trend entirely (not enough signal)
    if (validRoas.length < 5) {
        trend = 0;
    }

    // Dampen trend aggressively for low-signal/low-trust ads
    if (trustFactor < 0.7) {
        trend *= Math.pow(trustFactor, 1.5);
    }

    // POSITIVE TREND PENALTY: Positive trends are harder to prove than negative ones.
    if (trend > 0) trend *= 0.8;

    // ---- DETRENDED SEASONALITY ----
    const recentWindow = Math.min(28, dates.length);
    const recentSlice = fullDailyRoas.slice(-recentWindow);

    let localSum = 0, localCnt = 0;
    recentSlice.forEach((v) => { if (v !== null) { localSum += v; localCnt++; } });
    const globalMean = localCnt > 0 ? localSum / localCnt : 1;
    let smoothLvl = globalMean;
    const localLevels: number[] = [];
    recentSlice.forEach((v) => {
        if (v !== null) smoothLvl = 0.3 * v + 0.7 * smoothLvl;
        localLevels.push(smoothLvl);
    });

    const dowEffect = new Array(7).fill(0), dowCount = new Array(7).fill(0);
    recentSlice.forEach((val, i) => {
        if (val === null || localLevels[i] <= 0) return;
        const di = dates.length - recentWindow + i;
        if (di < 0 || di >= dates.length) return;
        const dow = new Date(dates[di]).getDay();
        dowEffect[dow] += val / localLevels[i];
        dowCount[dow]++;
    });
    for (let d = 0; d < 7; d++) {
        if (dowCount[d] > 0) {
            const rawEffect = dowEffect[d] / dowCount[d];
            // CAP SEASONALITY: No day can be > 50% stronger/weaker than average
            dowEffect[d] = Math.max(0.5, Math.min(1.5, rawEffect));
        } else {
            dowEffect[d] = 1;
        }
    }

    const lastDateObj = new Date(dates[dates.length - 1]);

    // ---- INCREMENTAL FATIGUE ----
    const currentAge = dates.length;
    const fatigueAtCurrentAge = Math.max(0.3, Math.pow(0.5, currentAge / halfLife));

    // ---- ECOMMERCE EXPERIENCE: DAMPED MOMENTUM ----
    // Only allow damped trend for scaling/testing phases with >= 7 data points AND positive trend.
    if (validRoas.length >= 7 && (phase.current === 'scaling' || phase.current === 'testing') && trend > 0) {
        phi = 0.70;
    } else {
        trend = 0;
    }

    // ---- ZERO-CRUSH PROTOCOL (Dead Ads) ----
    // If an ad has been paused, has basically no recent ROAS, or is stale, crush the baseline.
    let isDead = false;

    // 1. Are the most recent valid points functionally zero?
    // E-commerce can easily have 3-5 day dry spells on lower-budget ads. Checking 5 days is safer.
    if (validRoas.length >= 5) {
        const recent5 = validRoas.slice(-5);
        const recentSum = recent5.reduce((a, b) => a + b, 0);
        if (recentSum < 0.1) {
            isDead = true;
        }
    }

    // 3. Staleness Check: Have we seen absolutely no valid ROAS in the last 30 days?
    // Using 30 days to prevent falsely crushing ads in sporadic testing phases
    if (!isDead && fullDailyRoas.length >= 30) {
        const last30Days = fullDailyRoas.slice(-30);
        let hasRecentActivity = false;
        for (const val of last30Days) {
            if (val !== null && val > 0.05) {
                hasRecentActivity = true;
                break;
            }
        }
        if (!hasRecentActivity) {
            isDead = true;
        }
    }

    if (isDead) {
        level = 0;
        trend = 0;
    }

    const result: RawForecastPoint[] = [];
    for (let i = 0; i < days; i++) {
        const futureDate = addDays(lastDateObj, i + 1);
        const dow = futureDate.getDay();
        let dampedCum = 0;
        for (let j = 1; j <= i + 1; j++) dampedCum += Math.pow(phi, j) * trend;
        let base = level + dampedCum;

        // ---- PHASE-AWARE MEAN REVERSION ----
        // Over time, our "trust" in the ad-level ROAS fades, pulling back toward the account-wide average.
        // We revert faster for scaling/testing ads as their performance is usually peaky.
        let reversionSpeed = 0.02;
        if (phase.current === 'testing') reversionSpeed = 0.08;
        else if (phase.current === 'scaling') reversionSpeed = 0.05;
        else if (phase.current === 'stabilizing') reversionSpeed = 0.01;

        const maxReversion = 0.6;
        const reversionRate = maxReversion * (1 - Math.exp(-reversionSpeed * (i + 1)));

        // Zero-Crush: If the ad is dead, don't let it recover via mean reversion.
        if (isDead) {
            base = 0;
        } else {
            // Mean reversion toward the SOURCE's own average, NOT inflated prior.
            // In ecommerce, a product's ROAS is its own reality, not the account's.
            base = (1 - reversionRate) * base + reversionRate * localMean;
        }

        // Apply Safety Cap: ALWAYS enforce
        if (!isDead) {
            base = Math.min(base, softCap);
        }

        // Day-of-week seasonality (only if we have enough data to trust it)
        const seasonConf = validRoas.length >= 14 ? 1.0 : validRoas.length >= 7 ? 0.5 : 0.0;
        if (!isDead && seasonConf > 0) {
            base *= 1 + (dowEffect[dow] - 1) * seasonConf;
        }

        const fatigueAtFuture = Math.max(0.3, Math.pow(0.5, (currentAge + i) / halfLife));
        base *= fatigueAtFuture / fatigueAtCurrentAge;

        // ---- FINAL SAFETY CAPS (Layer 2) ----
        // ECOMMERCE: a 0.5x ROAS source should never forecast above 0.65x.
        const relativeCap = validRoas.length < 7 ? historicalMax * 1.3 : historicalMax * 1.5;
        base = Math.min(base, relativeCap);

        // Absolute global cap (extreme edge case guard)
        base = Math.min(base, 12.0);

        // Multiplicative noise 
        const safeBase = isDead ? 0 : Math.max(0.01, base);

        // ECOMMERCE NOISE: Sparse data → very tight noise. In ecommerce, daily ROAS
        // doesn't randomly double. Noise should be proportional to data confidence.
        // < 5 pts: ±15% max, 5-15 pts: ±25% max, 15+: ±40% max
        const sparseNoiseDamper = validRoas.length < 5 ? 0.3 : validRoas.length < 15 ? 0.6 : 1.0;
        const maxNoiseRatio = (validRoas.length < 5 ? 0.15 : validRoas.length < 15 ? 0.25 : 0.40);
        let noiseSample = 0;
        let margin = 0;

        if (!isDead && safeBase > 0.05) {
            noiseSample = normalRandom(`${sourceName}-${i}`) * safeBase * jitterCV * jitterNoiseMult * sparseNoiseDamper;
            noiseSample = Math.max(-maxNoiseRatio * safeBase, Math.min(maxNoiseRatio * safeBase, noiseSample));
            margin = safeBase * jitterCV * jitterBandMult * sparseNoiseDamper * Math.sqrt(1 + 0.08 * i);
        }

        // Tighter bands for ecommerce: the forecast line should look stable, not chaotic
        const upperLimit = isDead ? 0 : Math.max(0.01, safeBase + margin);
        const lowerLimit = isDead ? 0 : Math.max(0.01, safeBase - margin);

        result.push({
            baseline: safeBase,
            noise: noiseSample,
            upper: upperLimit,
            lower: lowerLimit
        });
    }
    return result;
};


// ============================================================
// PUBLIC API: computeRoasForecast
// ============================================================

export const computeRoasForecast = (
    finance: any,
    entities: BoardEntity[] | undefined,
    channels: any[] | undefined,
    forecastDays: number
): RoasForecastResult => {

    const txs = finance?.transactions?.filter((tx: any) => tx.isActive !== false) || [];

    // ============================================================
    // STAGE 1: HISTORICAL DATA EXTRACTION (AT THE CAMPAIGN LEVEL)
    // ============================================================

    const adEntityData = new Map<string, {
        name: string, platform: string, sourceName: string,
        dailySpend: Record<string, number>, dailyRev: Record<string, number>,
        publishedAt: string | null, firstSpendDay: string | null
    }>();
    const allDates = new Set<string>();

    txs.forEach((tx: any) => {
        const dateKey = tx.date?.split('T')[0];
        if (!dateKey) return;
        allDates.add(dateKey);

        const rawAmt = Number(tx.amount);
        const amt = isNaN(rawAmt) ? 0 : rawAmt;
        const traffic = tx.traffic ? (typeof tx.traffic === 'string' ? JSON.parse(tx.traffic) : tx.traffic) : {};

        // Determine platform-level source name for identification
        const sourceName = (tx.source || traffic.source || traffic.utm_source || 'Direct / Organic').toLowerCase();

        // Follow Traffic Page's mapping logic: prioritize findCampaignMatch for all paid traffic
        let matchedEntity: BoardEntity | null = null;
        if (traffic.isPaid || tx.type === 'EXPENSE') {
            matchedEntity = findCampaignMatch(traffic, entities);
        }

        let targetEntityId: string | null = null;
        let displayName = '';
        const primaryId = traffic.ad_id || traffic.adset_id || traffic.campaign_id || traffic.source || 'no-id';

        if (matchedEntity) {
            targetEntityId = String(matchedEntity.id);
            displayName = matchedEntity.name;
        } else if (traffic.isPaid || tx.type === 'EXPENSE') {
            // Unmatched paid source - group by identity to allow granular forecast even if not in board
            targetEntityId = `unmatched-${sourceName}-${primaryId}`;
            displayName = `${sourceName.charAt(0).toUpperCase() + sourceName.slice(1)} - ${primaryId}`;
        } else {
            // Organic
            targetEntityId = `source-${sourceName}`;
            displayName = sourceName.charAt(0).toUpperCase() + sourceName.slice(1);
        }

        if (!targetEntityId) return;

        if (!adEntityData.has(targetEntityId)) {
            const channel = matchedEntity ? channels?.find(c => c.id === matchedEntity?.channelId) : null;
            const platform = channel?.channelType?.toLowerCase() ||
                (sourceName.toLowerCase().includes('meta') ? 'meta' :
                    sourceName.toLowerCase().includes('facebook') ? 'meta' :
                        sourceName.toLowerCase().includes('instagram') ? 'meta' :
                            sourceName.toLowerCase().includes('tiktok') ? 'tiktok' :
                                sourceName.toLowerCase().includes('google') ? 'google' : 'default');

            const isMarketing = MARKETING_PLATFORMS.some(mp => platform.includes(mp));
            if (!isMarketing) return;

            adEntityData.set(targetEntityId, {
                name: displayName || sourceName,
                platform,
                sourceName,
                dailySpend: {}, dailyRev: {},
                publishedAt: matchedEntity?.publishedAt || null,
                firstSpendDay: null
            });
        }

        const ed = adEntityData.get(targetEntityId);
        if (!ed) return;

        if (tx.type === 'EXPENSE') {
            ed.dailySpend[dateKey] = (ed.dailySpend[dateKey] || 0) + Math.abs(amt);
            if (!ed.firstSpendDay || dateKey < ed.firstSpendDay) ed.firstSpendDay = dateKey;
        } else if (tx.type === 'REVENUE') {
            ed.dailyRev[dateKey] = (ed.dailyRev[dateKey] || 0) + amt;
        }
    });

    const sortedDates = Array.from(allDates).sort();
    if (sortedDates.length < 2) return { perSourceCharts: [], combinedChart: [], backtestScores: {}, jitterProfiles: {} };

    // Account-wide priors
    let totalAccountSpend = 0, totalAccountRev = 0;
    adEntityData.forEach(ed => {
        sortedDates.forEach(d => {
            totalAccountSpend += (ed.dailySpend[d] || 0);
            totalAccountRev += (ed.dailyRev[d] || 0);
        });
    });
    const accountPriorRoas = totalAccountSpend > 0 ? totalAccountRev / totalAccountSpend : 1.0;

    // ============================================================
    // STAGE 2: PER-AD FORECAST GENERATION (PHASE-AWARE)
    // ============================================================

    const perSourceCharts: RoasSourceForecast[] = [];
    const backtestScores: Record<string, number> = {};
    const jitterProfiles: Record<string, JitterProfile> = {};

    adEntityData.forEach((ed, entityId) => {
        // 2a. Detect Current Lifecycle Phase
        const fullDailySpend = sortedDates.map(d => ed.dailySpend[d] || 0);
        const fullDailyRev = sortedDates.map(d => ed.dailyRev[d] || 0);
        const phase = detectCurrentPhase(fullDailySpend, fullDailyRev);

        // ---- INVENTORY CLIFF LOGIC ----
        let consecutiveZeroRev = 0;
        const filteredDailyRoas = sortedDates.map(d => {
            const sp = ed.dailySpend[d] || 0;
            const rv = ed.dailyRev[d] || 0;
            if (sp <= 0) {
                consecutiveZeroRev = 0;
                return null;
            }
            if (rv <= 0) {
                consecutiveZeroRev++;
            } else {
                consecutiveZeroRev = 0;
            }

            // Spend > 50% of entity avg but 0 revenue = likely stock-out/tech issue
            // However, if it happens for > 2 days, it's not a glitch—the ad is dead/burning cash.
            const activeDays = fullDailySpend.filter(v => v > 0).length;
            const entityAvgSpend = fullDailySpend.reduce((a, b) => a + b, 0) / Math.max(1, activeDays);

            if (rv <= 0 && sp > entityAvgSpend * 0.5 && consecutiveZeroRev <= 2) {
                return null;
            }

            const r = rv / sp;
            return (isNaN(r) || !isFinite(r)) ? null : r;
        });
        const validRoas = filteredDailyRoas.filter((v): v is number => v !== null);
        if (validRoas.length < 1) return;

        // 2b. Compute Noise Profile
        const allRoasValues = filteredDailyRoas.map(v => v ?? 0);
        let lastNonNullIdx = -1;
        for (let i = filteredDailyRoas.length - 1; i >= 0; i--) { if (filteredDailyRoas[i] !== null) { lastNonNullIdx = i; break; } }
        const daysSinceLastRoas = filteredDailyRoas.length - 1 - lastNonNullIdx;

        const jp = computeAdaptiveJitter(allRoasValues, validRoas, sortedDates.length, daysSinceLastRoas, 0.4, false);

        // PAUSED OVERRIDE: No jitter, no optimism lean
        if (phase.current === 'paused') {
            jp.relativeCV = 0;
            jp.noiseMultiplier = 0;
            jp.bandWidthMultiplier = 0;
            jp.defaultSliderValue = 0;
            jp.defaultOptimism = 50;
            jp.effectiveSigma = 0;
        }

        jitterProfiles[entityId] = jp;

        let halfLife = fitFatigueHalfLife(validRoas) || getDefaultHalfLife(ed.platform);

        // ---- PHASE-AWARE FATIGUE CYCLE ----
        if (phase.current === 'testing') halfLife *= 0.5;
        if (phase.current === 'scaling') halfLife *= 0.8;
        if (phase.current === 'stabilizing') halfLife *= 1.4;

        const activityDensity = validRoas.length / Math.max(sortedDates.length, 1);
        const forecast = forecastFromSeries(validRoas, halfLife, ed.name, sortedDates, filteredDailyRoas, forecastDays, accountPriorRoas, jp.relativeCV, jp.noiseMultiplier, jp.bandWidthMultiplier, phase, activityDensity);

        // 2d. Backtesting
        let bScore: number | null = null;
        if (validRoas.length >= 4) {
            const btHoldout = 7;
            const btTrainValid = validRoas.slice(0, -btHoldout);
            const btActual = validRoas.slice(-btHoldout);
            if (btTrainValid.length >= 7) {
                const btForecast = forecastFromSeries(btTrainValid, halfLife, ed.name, sortedDates.slice(0, -btHoldout), filteredDailyRoas.slice(0, -btHoldout), btHoldout, accountPriorRoas, jp.relativeCV, jp.noiseMultiplier, jp.bandWidthMultiplier, phase, activityDensity);
                let err = 0, cnt = 0;
                btActual.forEach((act, i) => {
                    if (act !== null && btForecast[i]) { err += Math.abs(btForecast[i].baseline - act) / Math.max(0.01, act); cnt++; }
                });
                if (cnt > 0) {
                    const calculatedScore = (1 - (err / cnt)) * 100;
                    bScore = isNaN(calculatedScore) ? null : Math.max(0, Math.min(100, calculatedScore));
                }
            }
        }
        if (bScore !== null) backtestScores[entityId] = bScore;

        // 2e. Format Chart Data
        const displayDates = sortedDates.slice(-30);
        const lastDateObj = new Date(sortedDates[sortedDates.length - 1]);
        const data: RoasPoint[] = [];

        displayDates.forEach(date => {
            const sp = ed.dailySpend[date] || 0;
            const rv = ed.dailyRev[date] || 0;
            let roas = sp > 0 ? rv / sp : null;
            if (roas !== null && (isNaN(roas) || !isFinite(roas))) roas = null;
            const isLast = date === sortedDates[sortedDates.length - 1];
            data.push({
                label: format(parseISO(date), 'MMM d'),
                date,
                actualRoas: roas,
                actualSpend: sp,
                actualRevenue: rv,
                baselineRoas: isLast ? roas : null,
                stochasticNoise: 0,
                upperRoas: isLast ? roas : null,
                lowerRoas: isLast ? roas : null
            });
        });

        for (let i = 0; i < forecastDays; i++) {
            const fd = addDays(lastDateObj, i + 1);
            let baseline = forecast[i].baseline;
            if (isNaN(baseline) || !isFinite(baseline)) baseline = 0;

            data.push({
                label: format(fd, 'MMM d'),
                date: fd.toISOString().split('T')[0],
                actualRoas: null, actualSpend: null, actualRevenue: null,
                baselineRoas: baseline,
                stochasticNoise: forecast[i].noise,
                upperRoas: forecast[i].upper,
                lowerRoas: forecast[i].lower
            });
        }

        const recentSpend = fullDailySpend.slice(-14).filter(v => v > 0);
        let avgSpend = recentSpend.length > 0 ? recentSpend.reduce((a, b) => a + b, 0) / recentSpend.length : 0;
        if (phase.current === 'paused') avgSpend = 0;

        perSourceCharts.push({
            id: entityId,
            platform: ed.platform,
            source: ed.name, // CAMPAIGN NAME
            color: SOURCE_COLORS[ed.platform] || SOURCE_COLORS.default,
            data, halfLife, halfLifeSource: 'data-driven', backtestScore: bScore, avgSpend, jitterProfile: jp, phase
        });
    });

    // ============================================================
    // STAGE 3: COMBINED REVENUE-WEIGHTED ROAS
    // ============================================================

    const combinedChart: RoasPoint[] = [];
    const displayDates = sortedDates.slice(-30);
    const lastDateObj = new Date(sortedDates[sortedDates.length - 1]);

    displayDates.forEach(date => {
        let tSpend = 0, tRev = 0;
        adEntityData.forEach(ed => { tSpend += (ed.dailySpend[date] || 0); tRev += (ed.dailyRev[date] || 0); });
        let roas = tSpend > 0 ? tRev / tSpend : null;
        if (roas !== null && (isNaN(roas) || !isFinite(roas))) roas = null;
        const isLast = date === sortedDates[sortedDates.length - 1];
        combinedChart.push({
            label: format(parseISO(date), 'MMM d'),
            date, actualRoas: roas, actualSpend: tSpend, actualRevenue: tRev,
            baselineRoas: isLast ? roas : null, stochasticNoise: 0, upperRoas: isLast ? roas : null, lowerRoas: isLast ? roas : null
        });
    });

    for (let i = 0; i < forecastDays; i++) {
        const fd = addDays(lastDateObj, i + 1);
        let wSum = 0, wBase = 0, wNoise = 0, wUpper = 0, wLower = 0;
        perSourceCharts.forEach(src => {
            const pt = src.data.find(h => h.date === fd.toISOString().split('T')[0]);
            if (pt) {
                wSum += src.avgSpend;
                wBase += src.avgSpend * (pt.baselineRoas || 0);
                wNoise += src.avgSpend * (pt.stochasticNoise || 0);
                wUpper += src.avgSpend * (pt.upperRoas || 0);
                wLower += src.avgSpend * (pt.lowerRoas || 0);
            }
        });
        combinedChart.push({
            label: format(fd, 'MMM d'),
            date: fd.toISOString().split('T')[0],
            actualRoas: null, actualSpend: null, actualRevenue: null,
            baselineRoas: wSum > 0 ? wBase / wSum : 0,
            stochasticNoise: wSum > 0 ? wNoise / wSum : 0,
            upperRoas: wSum > 0 ? wUpper / wSum : 0,
            lowerRoas: wSum > 0 ? wLower / wSum : 0
        });
    }

    return { perSourceCharts, combinedChart, backtestScores, jitterProfiles };
};
